import { useState, useEffect } from 'react';
import { api } from '../services/api';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [filter, setFilter] = useState('');
  const [profileColors, setProfileColors] = useState({});
  const [showCreateProfileModal, setShowCreateProfileModal] = useState(false);
  const [newProfile, setNewProfile] = useState({ profile_id: '', name: '', color: '#3b82f6' });
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('');

  const formatLastLogin = (lastLogin) => {
    if (!lastLogin) return 'Never';
    const date = new Date(lastLogin);
    if (date.getFullYear() < 2000) return 'Never';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getLastLoginDetail = (lastLogin) => {
    if (!lastLogin) return 'Never';
    const date = new Date(lastLogin);
    if (date.getFullYear() < 2000) return 'Never';
    return lastLogin;
  };

  const filteredUsers = users.filter(user => {
    const searchTerm = filter.toLowerCase();
    return user.username.toLowerCase().includes(searchTerm) ||
           user.display_name.toLowerCase().includes(searchTerm) ||
           user.email.toLowerCase().includes(searchTerm);
  });

  const loadUsers = () => {
    setLoading(true);
    Promise.all([
      api.listUsers(),
      api.listProfiles()
    ])
      .then(([usersData, profilesData]) => {
        setUsers(usersData.result || []);
        const colors = {};
        (profilesData.result || []).forEach(profile => {
          colors[profile.name] = profile.color;
        });
        setProfileColors(colors);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleUserClick = (username) => {
    api.getUser(username)
      .then(data => setSelectedUser(data.result))
      .catch(err => setError(err.message));
  };

  const openCreateProfileFromUser = () => {
    setNewProfile({ profile_id: '', name: '', color: '#3b82f6' });
    setSelectedGroups(selectedUser.groups.map(g => ({
      name: g.name,
      description: g.description,
      full_ad_name: g.full_name
    })));
    setSelectedFilter('');
    setShowCreateProfileModal(true);
  };

  const removeFromSelected = (group) => {
    setSelectedGroups(selectedGroups.filter(g => g.full_ad_name !== group.full_ad_name));
  };

  const createProfile = () => {
    if (!newProfile.profile_id || !newProfile.name) {
      setError('Profile ID and Name are required');
      return;
    }
    
    const profileData = {
      ...newProfile,
      ad_groups: selectedGroups
    };
    
    api.createProfile(profileData)
      .then(() => {
        setShowCreateProfileModal(false);
        setSelectedUser(null);
        loadUsers();
      })
      .catch(err => setError(err.message));
  };

  const renderProfiles = (profiles, maxDisplay = 3) => {
    if (!profiles || profiles.length === 0) return <span className="text-muted">None</span>;
    
    const displayProfiles = profiles.slice(0, maxDisplay);
    const remainingCount = profiles.length - maxDisplay;
    
    return (
      <div className="profile-badges">
        {displayProfiles.map(profile => (
          <span 
            key={profile} 
            className="profile-badge"
            style={{ backgroundColor: profileColors[profile] || '#6b7280', color: 'white' }}
          >
            {profile}
          </span>
        ))}
        {remainingCount > 0 && (
          <span className="profile-badge profile-more">+{remainingCount}</span>
        )}
      </div>
    );
  };

  if (loading) return <div className="loading">Loading users...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <>
      <div className="card">
        <h2>Users</h2>
        <div className="filter-container">
          <input
            type="text"
            placeholder="Filter by username, display name, or email..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="filter-input"
          />
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Display Name</th>
              <th>Email</th>
              <th>Last Login</th>
              <th>Status</th>
              <th>Profiles</th>
              <th>Missing Groups</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.username} onClick={() => handleUserClick(user.username)} style={{cursor: 'pointer'}}>
                <td>{user.username}</td>
                <td>{user.display_name}</td>
                <td>{user.email}</td>
                <td>{formatLastLogin(user.last_login)}</td>
                <td>
                  <span className={`badge ${user.is_active ? 'active' : 'inactive'}`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>{renderProfiles(user.profiles)}</td>
                <td>
                  {user.missing_groups && user.missing_groups.length > 0 ? (
                    <span className="text-warning">{user.missing_groups.length} missing</span>
                  ) : (
                    <span className="text-success">Complete</span>
                  )}
                </td>
              </tr>
            ))
          }
          </tbody>
        </table>
      </div>

      {selectedUser && (
        <div className="modal-overlay" onClick={() => setSelectedUser(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>User Details</h2>
              <button className="modal-close" onClick={() => setSelectedUser(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Username</span>
                  <span className="detail-value">{selectedUser.username}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Display Name</span>
                  <span className="detail-value">{selectedUser.display_name}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Email</span>
                  <span className="detail-value">{selectedUser.email}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Last Login</span>
                  <span className="detail-value">{getLastLoginDetail(selectedUser.last_login)}</span>
                </div>
              </div>
              
              {selectedUser.profiles && selectedUser.profiles.length > 0 && (
                <div style={{marginTop: '1.5rem'}}>
                  <span className="detail-label">Profiles</span>
                  <div className="profile-badges" style={{marginTop: '0.5rem'}}>
                    {selectedUser.profiles.map(profile => (
                      <span 
                        key={profile} 
                        className="profile-badge"
                        style={{ backgroundColor: profileColors[profile] || '#6b7280', color: 'white' }}
                      >
                        {profile}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedUser.groups && selectedUser.groups.length > 0 && (
                <div style={{marginTop: '1.5rem'}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem'}}>
                    <span className="detail-label">Groups</span>
                    <button className="btn-primary" onClick={openCreateProfileFromUser}>
                      Create Profile from User
                    </button>
                  </div>
                  <div className="group-list">
                    {selectedUser.groups.map((group, idx) => {
                      const isMissing = selectedUser.missing_groups && selectedUser.missing_groups.includes(group.full_name);
                      return (
                        <div key={idx} className={`group-item ${isMissing ? 'group-missing' : ''}`}>
                          <div className="group-name">
                            {isMissing && <span className="missing-icon">⚠️</span>}
                            {group.name}
                          </div>
                          {group.description && <div className="group-desc">{group.description}</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showCreateProfileModal && (
        <div className="modal-overlay" onClick={() => setShowCreateProfileModal(false)}>
          <div className="modal-content group-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Profile from {selectedUser?.display_name}</h2>
              <button className="modal-close" onClick={() => setShowCreateProfileModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-fields">
                <div className="form-field">
                  <label className="form-label">Profile ID</label>
                  <input
                    type="text"
                    value={newProfile.profile_id}
                    onChange={(e) => setNewProfile({...newProfile, profile_id: e.target.value})}
                    className="form-input"
                    placeholder="Enter profile ID"
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    value={newProfile.name}
                    onChange={(e) => setNewProfile({...newProfile, name: e.target.value})}
                    className="form-input"
                    placeholder="Enter profile name"
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">Color</label>
                  <input
                    type="color"
                    value={newProfile.color}
                    onChange={(e) => setNewProfile({...newProfile, color: e.target.value})}
                    className="color-input"
                  />
                </div>
              </div>
              
              <div style={{marginTop: '1rem'}}>
                <h3>Selected Groups (click to remove)</h3>
                <input
                  type="text"
                  placeholder="Filter groups..."
                  value={selectedFilter}
                  onChange={(e) => setSelectedFilter(e.target.value)}
                  className="group-filter-input"
                />
                <div className="group-list-box" style={{maxHeight: '300px'}}>
                  {selectedGroups
                    .filter(group => group.name.toLowerCase().includes(selectedFilter.toLowerCase()))
                    .map((group, idx) => (
                      <div key={idx} className="group-item-selectable" onClick={() => removeFromSelected(group)}>
                        <div className="group-name">{group.name}</div>
                        <div className="group-desc">{group.full_ad_name}</div>
                      </div>
                    ))
                  }
                </div>
              </div>
              
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowCreateProfileModal(false)}>Cancel</button>
                <button className="btn-primary" onClick={createProfile}>Create Profile</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
